import type { BadgeTone, IconName } from "stories/Admin";
import { normalizeCategory, validateCategories } from "./campaignCategories";
import { parseAmounts } from "./campaignPayment";

/**
 * De tre kampanjetypene (se innsamlingskontrakten):
 * - IN_APP: gaver rett i appen med moskeens egen Vipps og/eller kort (Stripe).
 * - LINK: knappen åpner en lenke. iBønn ser ikke beløpene.
 * - INFO: bare informasjon, ingen betaling.
 */
export type CollectionMode = "IN_APP" | "LINK" | "INFO";

export const COLLECTION_MODES: CollectionMode[] = ["IN_APP", "LINK", "INFO"];

export interface CampaignKindInfo {
  /** Tittel på valgkortet i skjemaet. */
  title: string;
  /** Kort navn på merket i listen. */
  badge: string;
  description: string;
  /** Tydelig merknad om hvordan betalingen skjer (vises uthevet på valgkortet). */
  note?: { text: string; kind: "ok" | "warn" };
  tone: BadgeTone;
  icon: IconName;
}

export const CAMPAIGN_KINDS: Record<CollectionMode, CampaignKindInfo> = {
  IN_APP: {
    title: "Gaver i appen",
    badge: "Gaver i appen",
    description:
      "Folk gir med Vipps eller kort rett i iBønn. Pengene går rett til moskeens egen Vipps/Stripe. Du ser hvor mye som er samlet inn.",
    note: {
      text: "Bruker moskeens egen Vipps- og Stripe-avtale (API). Fremdrift, kvittering og oversikt i iBønn.",
      kind: "ok",
    },
    tone: "mint",
    icon: "heart",
  },
  LINK: {
    title: "Lenke til innsamling",
    badge: "Lenke",
    description:
      "Knappen åpner en lenke, f.eks. moskeens Vipps-side eller egen innsamlingsside.",
    note: {
      text: "Ingen Vipps- eller Stripe-kobling: knappen åpner bare lenken. iBønn ser ikke beløpene, og det blir ingen fremdrift eller oversikt.",
      kind: "warn",
    },
    tone: "blue",
    icon: "link",
  },
  INFO: {
    title: "Bare informasjon",
    badge: "Bare informasjon",
    description: "Vis et prosjekt eller mål uten betaling i appen.",
    tone: "gold",
    icon: "info",
  },
};

interface KindSource {
  collectionMode?: string | null;
  vippsEnabled?: boolean | null;
  stripeEnabled?: boolean | null;
  link?: string | null;
}

const isMode = (value: unknown): value is CollectionMode =>
  typeof value === "string" && (COLLECTION_MODES as string[]).includes(value);

/**
 * Typen til en kampanje. Bruker `collectionMode` fra serveren når den finnes;
 * ellers (eldre server) avledes den fra flaggene på samme måte som
 * backfillen: Vipps/Stripe → IN_APP, ellers lenke → LINK, ellers INFO.
 */
export const deriveCollectionMode = (c: KindSource): CollectionMode => {
  if (isMode(c.collectionMode)) return c.collectionMode;
  if (c.vippsEnabled || c.stripeEnabled) return "IN_APP";
  if (c.link && c.link.trim()) return "LINK";
  return "INFO";
};

/** Godtar bare fullstendige https-lenker, f.eks. «https://qr.vipps.no/…». */
export const isHttpsUrl = (value: string | null | undefined) => {
  if (!value) return false;
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" && !!url.hostname.includes(".");
  } catch {
    return false;
  }
};

const nok = new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 });

/** 38000 → «38 000 kr» (hele kroner, norsk tallformat). */
export const formatKr = (kroner: number | null | undefined) =>
  `${nok.format(Math.round(Number(kroner) || 0))} kr`;

/** Øre fra API-et → «38 000 kr». */
export const formatOre = (ore: number | null | undefined) =>
  formatKr((Number(ore) || 0) / 100);

/** «1 giver», «24 givere». */
export const formatDonors = (count: number) =>
  `${nok.format(count)} ${count === 1 ? "giver" : "givere"}`;

/** Andel av målet i prosent, 0–100. Uten mål: 0. */
export const progressPercent = (
  raisedOre: number | null | undefined,
  targetKr: number | null | undefined,
) => {
  if (!targetKr || targetKr <= 0 || !raisedOre || raisedOre <= 0) return 0;
  return Math.min(100, Math.round((raisedOre / 100 / targetKr) * 100));
};

/**
 * «38 000 av 150 000 kr · 24 givere» (samme tekst som i appene).
 * Uten mål: «38 000 kr · 24 givere».
 */
export const progressLabel = (
  raisedOre: number,
  targetKr: number | null | undefined,
  donorCount?: number | null,
) => {
  const raised =
    targetKr && targetKr > 0
      ? `${nok.format(Math.round(raisedOre / 100))} av ${formatKr(targetKr)}`
      : formatOre(raisedOre);
  return donorCount != null
    ? `${raised} · ${formatDonors(donorCount)}`
    : raised;
};

/** Verdiene i kampanjeskjemaet. */
export interface CampaignFormValues {
  /** Tom til typen er valgt i et nytt skjema. */
  collectionMode: CollectionMode | "";
  name: string;
  description: string;
  /** Mål i kroner. Tom eller 0 = uten mål. */
  targetAmount: number | "";
  activated: boolean;
  link: string;
  priority?: number;
  vippsEnabled: boolean;
  stripeEnabled: boolean;
  oneTimeEnabled: boolean;
  monthlyEnabled: boolean;
  /** Tekst i skjemaet, f.eks. «100, 200, 500». */
  suggestedAmounts: string;
  showProgress: boolean;
  /** Kategorier giveren velger mellom (bare gaver i appen). Tom = ingen. */
  categories: string[];
}

export const MAX_DESCRIPTION_LENGTH = 255;

/** Like verdier for sammenligning: tomt mål = 0, manglende prioritet = 0. */
const comparable = (v: CampaignFormValues) => ({
  ...v,
  targetAmount: v.targetAmount === "" ? 0 : Number(v.targetAmount),
  priority: v.priority ?? 0,
  categories: v.categories ?? [],
});

/**
 * Om skjemaet er endret siden det ble åpnet. Brukes for å spørre før
 * endringene forkastes (lukk, Avbryt, Escape, klikk utenfor, last siden på nytt).
 */
export const isCampaignFormDirty = (
  initial: CampaignFormValues,
  values: CampaignFormValues,
) => {
  const a = comparable(initial);
  const b = comparable(values);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).some((key) => {
    const x = a[key as keyof typeof a];
    const y = b[key as keyof typeof b];
    if (Array.isArray(x) || Array.isArray(y)) {
      const xs = Array.isArray(x) ? x : [];
      const ys = Array.isArray(y) ? y : [];
      return xs.length !== ys.length || xs.some((item, i) => item !== ys[i]);
    }
    return x !== y;
  });
};

/** Hva som er koblet til under Betaling. `null` = ukjent (ikke lastet). */
export interface ConnectedProviders {
  vipps: boolean;
  stripe: boolean;
}

/**
 * Feil i skjemaet, per felt. Samme regler som serveren: gaver i appen trenger
 * minst én betalingsmåte (som er koblet til) og minst én gavetype; lenke
 * trenger en gyldig https-lenke.
 */
export const validateCampaignForm = (
  values: CampaignFormValues,
  connected: ConnectedProviders | null = null,
): Partial<Record<keyof CampaignFormValues, string>> => {
  const errors: Partial<Record<keyof CampaignFormValues, string>> = {};
  if (!values.collectionMode) {
    errors.collectionMode = "Velg hva slags kampanje dette er.";
  }
  if (!values.name.trim()) errors.name = "Navn er påkrevd";
  if (!values.description.trim()) {
    errors.description = "Beskrivelse er påkrevd";
  } else if (values.description.length > MAX_DESCRIPTION_LENGTH) {
    errors.description = `Maks ${MAX_DESCRIPTION_LENGTH} tegn tillatt`;
  }
  const target = values.targetAmount === "" ? 0 : Number(values.targetAmount);
  if (!Number.isFinite(target) || target < 0) {
    errors.targetAmount = "Målet må være 0 kr eller mer";
  }

  if (values.collectionMode === "IN_APP") {
    if (!values.vippsEnabled && !values.stripeEnabled) {
      errors.vippsEnabled = "Velg Vipps, kort eller begge.";
    } else if (connected) {
      const missing = [
        values.vippsEnabled && !connected.vipps ? "Vipps" : null,
        values.stripeEnabled && !connected.stripe ? "Kort (Stripe)" : null,
      ].filter(Boolean);
      if (missing.length) {
        errors.vippsEnabled = `${missing.join(" og ")} er ikke koblet til moskeen. Koble til under Betaling, eller fjern haken.`;
      }
    }
    if (!values.oneTimeEnabled && !values.monthlyEnabled) {
      errors.oneTimeEnabled =
        "Velg engangsgave, fast månedlig gave eller begge.";
    }
    const categoriesError = validateCategories(values.categories ?? []);
    if (categoriesError) errors.categories = categoriesError;
    if (parseAmounts(values.suggestedAmounts) === null) {
      errors.suggestedAmounts =
        "Skriv hele kroner fra 10 til 100 000, skilt med komma.";
    }
  }

  if (values.collectionMode === "LINK") {
    if (!values.link.trim()) {
      errors.link = "Lim inn lenken knappen skal åpne.";
    } else if (!isHttpsUrl(values.link)) {
      errors.link = "Lenken må være en hel adresse som starter med https://";
    }
  }
  return errors;
};

/**
 * Feltene som sendes til serveren. Bare feltene som hører til typen beholdes,
 * resten nullstilles, så det ikke ligger igjen gamle lenker eller
 * betalingsvalg som ikke vises i skjemaet.
 */
export const campaignPayload = (values: CampaignFormValues) => {
  const mode = values.collectionMode || "INFO";
  const inApp = mode === "IN_APP";
  return {
    collectionMode: mode,
    name: values.name.trim(),
    description: values.description.trim(),
    targetAmount: values.targetAmount === "" ? 0 : Number(values.targetAmount),
    activated: values.activated,
    priority: values.priority,
    link: mode === "LINK" ? values.link.trim() : "",
    vippsEnabled: inApp && values.vippsEnabled,
    stripeEnabled: inApp && values.stripeEnabled,
    oneTimeEnabled: inApp && values.oneTimeEnabled,
    monthlyEnabled: inApp && values.monthlyEnabled,
    suggestedAmounts: inApp
      ? (parseAmounts(values.suggestedAmounts) ?? [])
      : [],
    showProgress: inApp && values.showProgress,
    categories: inApp ? (values.categories ?? []).map(normalizeCategory) : [],
  };
};

/** «Vipps og kort · engangsgave og fast månedlig gave». */
export const inAppSummary = (c: {
  vippsEnabled?: boolean | null;
  stripeEnabled?: boolean | null;
  oneTimeEnabled?: boolean | null;
  monthlyEnabled?: boolean | null;
}) => {
  const providers = [
    c.vippsEnabled ? "Vipps" : null,
    c.stripeEnabled ? "kort" : null,
  ].filter(Boolean) as string[];
  const intervals = [
    c.oneTimeEnabled ? "engangsgave" : null,
    c.monthlyEnabled ? "fast månedlig gave" : null,
  ].filter(Boolean) as string[];
  const p = providers.join(" og ");
  const who = p
    ? p.charAt(0).toUpperCase() + p.slice(1)
    : "Ingen betalingsmåte";
  return intervals.length ? `${who} · ${intervals.join(" og ")}` : who;
};
